/**
 * 11.2 — a experiência do Cliente/Anunciante (lote P2).
 *
 * ## As duas leis de recorte, que não são a mesma
 *
 * **O período governa as campanhas**, e a regra é INTERSEÇÃO: uma campanha de 10/06 a
 * 15/08 aparece em julho porque a janela dela cruza julho — não porque começou nele.
 *
 * **A posição fiscal NÃO responde ao período** (spec 05 §3, exceção 2). Pendência não
 * prescreve: uma NF vencida em maio continua vencida em agosto, e escondê-la porque o
 * filtro está em agosto seria o sistema ajudando a esquecer. O mesmo vale para o KPI de
 * documentos, que conta o ACERVO.
 *
 * Isso está escrito aqui em duas chaves de consulta diferentes: a das campanhas inclui
 * o período, a dos pedidos e documentos não. Trocar o período re-busca uma e não toca as
 * outras — e é por isso que a lei é visível no código, e não só na prosa.
 *
 * ## Anunciante puro não vê NF
 *
 * O bloco fiscal não é escondido por um `if` de papel: ele não existe quando a lista
 * vem vazia, e ela vem vazia porque o modelo de leitura só materializa pedidos em que a
 * empresa é o Cliente FATURADO (R-06/DS-29). O corte é do dado, não da tela.
 *
 * ## "Todas" num portal de várias empresas (D-131)
 *
 * A campanha e o documento chegam UMA vez, deduplicados pela API (DS-527): somar aqui o
 * investimento e contar os documentos continua certo. O que a tela acrescenta é dizer de
 * qual empresa é cada coisa, e marcar a campanha que nenhuma empresa do portal contratou.
 */
import { useEffect, useMemo, useState } from 'react'
import { FileText } from 'lucide-react'
import {
  campanhaDoClienteNoPortal,
  documentoDoPortal,
  pedidoDoClienteNoPortal,
  type CampanhaDoClienteNoPortal,
  type PedidoDoClienteNoPortal,
} from '@dv/contracts'
import { z } from 'zod'
import { resolverPeriodo, type DataNominal, type Periodo } from '@dv/domain'
import { pedir } from '../api/cliente.js'
import { useConsulta, juntar } from '../estado/recurso.js'
import { Erro, Esqueleto, Vazio } from '../componentes/estados.js'
import { rotuloCompleto, SeletorDePeriodo } from '../componentes/periodo.js'
import { SeletorComCapa } from '../componentes/capa.js'
import { useEmpresas } from '../estado/empresas.js'
import { br, dataBr, somar } from '../formato.js'
// P-11 (D-189, DS-658): a cor do selo pela situação; a tabela mora com a leitura dos resultados, porque o cartão do Parceiro a usa também.
import { avisoDaProgramada, COR_DA_CAMPANHA } from '../campanha-no-portal.js'
import { ResultadosDaCampanha, RotuloDosResultados } from '../componentes/resultados-da-campanha.js'
import { DicaDoPortal } from '../componentes/dica.js'
import { Documentos } from './documentos.js'

const listaDeCampanhas = z.array(campanhaDoClienteNoPortal)
const listaDePedidos = z.array(pedidoDoClienteNoPortal)
const listaDeDocumentos = z.array(documentoDoPortal)

/** DS-47: quem pagou foi ele. A tela do cliente diz "Pago", nunca "Recebido". */
function situacaoNaVozDoCliente(situacao: string): string {
  return situacao === 'Recebido' ? 'Pago' : situacao
}

const COR_DA_SITUACAO: Readonly<Record<string, string>> = {
  Vencido: 'vermelho',
  'NF em emissão': 'navy',
  'A faturar': 'ambar',
  Pago: 'verde',
}

/** O que o selo da campanha escreve (P-11): a situação, e o status antigo só sem ela (`situacao ?? status`). */
function situacaoDaCampanha(campanha: CampanhaDoClienteNoPortal): string {
  return campanha.situacao ?? campanha.status
}

/**
 * A campanha no ar, para o KPI: pela situação, "Em andamento" (a Pausada não conta, P-11); sem ela, o status antigo
 * 'Veiculando', que ainda junta a Pausada, como a linha da versão anterior.
 */
function campanhaEmAndamento(campanha: CampanhaDoClienteNoPortal): boolean {
  return campanha.situacao === undefined || campanha.situacao === null
    ? campanha.status === 'Veiculando'
    : campanha.situacao === 'Em andamento'
}

export function ExperienciaDoCliente({ hoje, aoMudarPeriodo }: {
  hoje: DataNominal
  /** O rótulo do período sobe para a linha do "Bem-vindo", na casca (PK-06A). */
  aoMudarPeriodo: (rotulo: string) => void
}) {
  const [periodo, setPeriodo] = useState<Periodo>(() => resolverPeriodo('este_mes', hoje))
  const rotuloDoPeriodo = rotuloCompleto(periodo)
  useEffect(() => {
    aoMudarPeriodo(rotuloDoPeriodo)
  }, [aoMudarPeriodo, rotuloDoPeriodo])
  const [status, setStatus] = useState('')
  const empresas = useEmpresas()

  const campanhas = useConsulta(`campanhas|${periodo.de}|${periodo.ate}|${status}`, (sinal) => {
    const parametros = new URLSearchParams({ de: periodo.de, ate: periodo.ate })
    if (status !== '') parametros.set('status', status)
    void sinal
    return pedir(`cliente/campanhas?${parametros.toString()}`, listaDeCampanhas)
  })
  // Sem período na chave: a posição fiscal e o acervo não prescrevem.
  const pedidos = useConsulta('cliente/pedidos', () => pedir('cliente/pedidos', listaDePedidos))
  const documentos = useConsulta('cliente/documentos', () =>
    pedir('cliente/documentos', listaDeDocumentos),
  )

  /*
    O acervo de campanhas SEM recorte — e só quando o recorte não devolve nada.

    "Ajuste o filtro ou o período" não diz o que ajustar: quem está do outro lado não
    sabe se a empresa nunca teve campanha nenhuma ou se a última terminou na semana
    passada. A resposta é justamente uma campanha que o recorte escondeu, e ela só pode
    vir de um pedido sem recorte — que por isso só sai quando a lista da tela está
    vazia, e não em toda visita.

    Fora do `juntar` de propósito: isto decora uma AUSÊNCIA, e uma falha aqui não pode
    derrubar a tela inteira por causa de uma frase.
  */
  const semCampanhas = campanhas.recurso.estado === 'pronto' && campanhas.recurso.dados.length === 0
  const acervo = useConsulta<readonly CampanhaDoClienteNoPortal[] | null>(
    semCampanhas ? 'cliente/campanhas|acervo' : 'cliente/campanhas|acervo|dispensado',
    () => (semCampanhas ? pedir('cliente/campanhas', listaDeCampanhas) : Promise.resolve(null)),
  )

  const conjunto = juntar([campanhas.recurso, pedidos.recurso, documentos.recurso])

  if (conjunto !== 'carregando' && conjunto !== 'pronto') {
    return (
      <Erro
        tipo={conjunto}
        tentar={() => {
          campanhas.recarregar()
          pedidos.recarregar()
          documentos.recarregar()
        }}
      />
    )
  }

  const listaCampanhas = campanhas.recurso.estado === 'pronto' ? campanhas.recurso.dados : []
  const listaPedidos = pedidos.recurso.estado === 'pronto' ? pedidos.recurso.dados : []
  const listaDocumentos = documentos.recurso.estado === 'pronto' ? documentos.recurso.dados : []
  const carregando = conjunto === 'carregando'
  const dadosDoAcervo = acervo.recurso.estado === 'pronto' ? acervo.recurso.dados : null
  const ultima = dadosDoAcervo === null ? null : ultimaVeiculacaoEncerrada(dadosDoAcervo)
  /*
    Dois vazios diferentes pedem palavras diferentes: "esta empresa nunca teve campanha"
    não é "o recorte de hoje não achou nada". O acervo só é pedido quando a lista da tela
    está vazia, então um acervo RESPONDIDO e vazio aqui é a empresa inteira, não o filtro.
    `null` guarda a diferença entre "ainda não perguntei" e "perguntei e não há nada".
  */
  const semAcervo = dadosDoAcervo !== null && dadosDoAcervo.length === 0
  const tituloDoVazio = semAcervo
    ? 'Nenhuma campanha ainda'
    : status === ''
      ? 'Nenhuma campanha neste período'
      : 'Nenhuma campanha neste status'
  /*
    Sem acervo, mandar ajustar o filtro é conselho que não leva a lugar nenhum: não existe
    recorte desta empresa que devolva campanha, e o título ao lado acabou de dizer isso.
    Era o mesmo defeito que este bloco veio corrigir, sobrevivendo no caso mais extremo.

    O `semAcervo` vem primeiro porque acervo vazio implica `ultima === null`: sem esta
    ordem, o ramo do "ajuste o filtro" responderia pelos dois casos, como respondia antes.
  */
  const textoDoVazio = semAcervo
    ? 'Assim que a primeira campanha for veiculada, ela aparece aqui.'
    : ultima === null
      ? 'Ajuste o filtro ou o período.'
      : `A última veiculação (${ultima.codigo}) foi concluída em ${dataBr(ultima.fim)}. Ajuste o filtro ou o período.`

  return (
    <>
      {/*
        Enquanto carrega, ESQUELETO — nunca "R$ 0,00". O zero é uma afirmação sobre o
        dinheiro do cliente, e ela não é verdade antes da resposta.
      */}
      {carregando ? (
        <Esqueleto linhas={3} teste="esqueleto-kpis" />
      ) : (
        <div className="kpis" data-teste="kpis">
          <Kpi
            rotulo="Campanhas no período"
            valor={String(listaCampanhas.length)}
            apoio={`${String(listaCampanhas.filter(campanhaEmAndamento).length)} em andamento`}
          />
          <Kpi
            rotulo="Investimento no período"
            valor={br(somar(listaCampanhas.map((c) => c.valor)))}
            apoio={pvsDistintos(listaCampanhas)}
          />
          {/* "(todos)" diz que o acervo não responde ao período, como os dois ao lado respondem (D-203). */}
          <Kpi rotulo="Documentos (todos)" valor={String(listaDocumentos.length)} />
        </div>
      )}

      <section className="painel campanhas" data-teste="campanhas">
        <div className="titulo">
          {/*
            O título do protótipo (11.2), sem eyebrow acima: a seção já se diz pelo h2 (PK-09). Abaixo dele, o rótulo fixo
            dos resultados, uma vez para a seção e não em cada cartão (D-203), quando algum cartão os traz.
          */}
          <div className="cabeca-da-secao">
            <h2>Campanhas</h2>
            <RotuloDosResultados resultados={listaCampanhas.map((campanha) => campanha.resultados)} />
          </div>
          <div className="filtros">
            <SeletorDePeriodo hoje={hoje} periodo={periodo} aoTrocar={setPeriodo} />
            <label className="filtro">
              {/* O protótipo não escreve rótulo acima do seletor: o nome segue para o leitor de tela. */}
              <span className="sr-only">Status</span>
              <SeletorComCapa
                value={status}
                onChange={(evento) => {
                  setStatus(evento.target.value)
                }}
                data-teste="filtro-status-campanha"
              >
                <option value="">Todas as campanhas</option>
                <option value="Veiculando">Veiculando</option>
                <option value="Concluída">Concluídas</option>
              </SeletorComCapa>
            </label>
          </div>
        </div>

        {campanhas.recurso.estado === 'carregando' ? (
          <Esqueleto linhas={3} teste="esqueleto-campanhas" />
        ) : listaCampanhas.length === 0 ? (
          <Vazio titulo={tituloDoVazio} texto={textoDoVazio} />
        ) : (
          /*
            Uma campanha por linha, no cartão LEVE do app interno (contatos.tsx:132-150): filete,
            raio 12 e sem sombra, porque ele já mora dentro de um cartão (D-134 · DS-531).
          */
          <div className="lista-cartoes">
            {listaCampanhas.map((campanha) => (
              <article key={campanha.campanhaId} className="cartao-item" data-teste="cartao-campanha">
                <div className="cartao-topo">
                  <p className="eyebrow">
                    {campanha.codigo}
                    {campanha.refExterna === null ? '' : ` · Ref. ${campanha.refExterna}`}
                  </p>
                  <span className={`badge ${COR_DA_CAMPANHA[situacaoDaCampanha(campanha)] ?? 'slate'}`} data-teste="situacao-da-campanha">{situacaoDaCampanha(campanha)}</span>
                </div>
                <h3 title={campanha.nome}>{campanha.nome}</h3>
                {empresas.mostrar && (
                  <p className="empresas-do-cartao" data-teste="empresas-da-campanha">
                    {empresas.nomesDe(campanha.empresaIds)}
                  </p>
                )}
                {/*
                  A Programada com veiculação já publicada (o DV360 veiculou antes do início planejado, ou antes de a
                  DigitalView registrar o início) não diz "Ainda não começou a veicular" ao lado das impressões: o
                  status local não esconde o gasto (D-191), e o aviso vira o início planejado (DS-677 (g)).
                */}
                {campanha.status === 'Programada' && (
                  <p className="aviso-programada" data-teste="aviso-programada">{avisoDaProgramada(campanha.inicio, campanha.resultados)}</p>
                )}
                {/*
                  Os resultados publicados (§5.8, P-47): só com o interruptor do público ligado. `null` (não publicado,
                  ou o pedido cancelado) e a resposta da versão anterior, sem o campo, dão o cartão de sempre.
                */}
                {campanha.resultados === undefined || campanha.resultados === null ? null : (
                  <ResultadosDaCampanha resultados={campanha.resultados} />
                )}
                <p className="cartao-rodape" data-teste="rodape-da-campanha">
                  {/* Nenhuma empresa do portal é o Cliente do pedido: a campanha aparece porque
                      uma delas é o Anunciante. O cartão diz isso sem nomear quem contratou (D-131). */}
                  {campanha.contratadaPorOutraEmpresa && (
                    <>
                      <span className="contratada-por-outra" data-teste="contratada-por-outra-empresa">
                        Contratada por outra empresa
                      </span>
                      {' · '}
                    </>
                  )}
                  {campanha.inicio === campanha.fim ? dataBr(campanha.inicio) : <>{dataBr(campanha.inicio)} — {dataBr(campanha.fim)}</>}
                  {' · investimento '}
                  <span className="valor tnum">{br(campanha.valor)}</span>
                  {campanha.nota === null ? null : ` · ${campanha.nota}`}
                </p>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* R-06: para anunciante puro a lista vem vazia e o bloco NÃO existe — não é
          estado vazio, é ausência. O corte é do modelo de leitura. */}
      {listaPedidos.length > 0 && <PosicaoFiscal pedidos={listaPedidos} hoje={hoje} />}

      {documentos.recurso.estado === 'carregando' ? (
        <Esqueleto linhas={4} teste="esqueleto-documentos" />
      ) : (
        <Documentos itens={listaDocumentos} comAbas titulo="Documentos" />
      )}
    </>
  )
}

function PosicaoFiscal({ pedidos, hoje }: { pedidos: readonly PedidoDoClienteNoPortal[]; hoje: DataNominal }) {
  const empresas = useEmpresas()
  const emAberto = useMemo(
    () => {
      const saldos = pedidos.map(p => p.saldoAberto)
      if (saldos.some(saldo => saldo === null)) return null
      return somar(saldos.filter((saldo): saldo is string => saldo !== null))
    },
    [pedidos],
  )

  return (
    <section className="painel" data-teste="posicao-fiscal">
      <div className="titulo com-apoio">
        <div>
          {/*
            O recorte que o fundador não conseguiu ler sem ajuda (item 16 · PK-06A). Fica à vista o que decide a leitura
            (contra quem os pedidos foram faturados), abaixo do h2, em 12px, como o apoio de seção do app interno
            (dre.tsx:197-199). Onde ficam as campanhas contratadas por outra empresa é explicação, e vai para a dica do
            grupo, ao lado do título e fora dele (D-203).
          */}
          <div className="titulo-com-dica">
            <h2>Pedidos e notas fiscais</h2>
            <DicaDoPortal rotulo="Pedidos e notas fiscais">
              {empresas.mostrar
                ? 'Campanhas contratadas por outra empresa em nome das empresas deste portal aparecem em Campanhas, e as notas ficam com quem foi faturado.'
                : 'Campanhas contratadas por outra empresa em seu nome aparecem em Campanhas, e as notas ficam com quem foi faturado.'}
            </DicaDoPortal>
          </div>
          <p className="apoio-da-secao" data-teste="recorte-fiscal">
            {empresas.mostrar ? 'Pedidos faturados contra as empresas deste portal.' : 'Pedidos faturados contra a sua empresa.'}
          </p>
        </div>
        <FileText size={20} className="icone-da-secao" aria-hidden="true" />
      </div>
      <div className="tabela dv-xscroll">
        <table className="dv-tabela" data-teste="tabela-posicao-fiscal">
          <thead>
            <tr>
              <th className="dv-elastica">Pedido</th>
              {/* Em Todas, a empresa faturada. Sem classe: a elástica é o Pedido, que carrega o título (D-124). */}
              {empresas.mostrar && <th>Empresa</th>}
              <th>Data da venda</th>
              <th>Nota fiscal</th>
              <th>Vencimento</th>
              <th>Valor</th>
              <th>Situação</th>
            </tr>
          </thead>
          <tbody>
            {pedidos.map((pedido) => {
              const situacao = situacaoNaVozDoCliente(pedido.situacao)
              const vencimento = vencimentoDoPedido(pedido)
              const vencido = pedido.saldoAberto !== null && vencimento !== null && vencimento < hoje && situacao !== 'Pago'
              return (
                <tr key={pedido.pedidoId} data-teste="linha-fiscal">
                  <td className="dv-elastica">
                    <span className="codigo tnum">{pedido.codigo}</span>
                    <span className="linha-apoio">{pedido.titulo}</span>
                  </td>
                  {empresas.mostrar && <td data-teste="empresa-da-linha">{empresas.nomeDe(pedido.empresaId)}</td>}
                  <td className="tnum">{dataBr(pedido.dataVenda)}</td>
                  <td className="tnum">{rotuloDasNotas(pedido)}</td>
                  <td className={vencido ? 'tnum vencido' : 'tnum'}>
                    {vencimento === null ? '—' : dataBr(vencimento)}
                  </td>
                  <td className="tnum valor">{br(pedido.valor)}</td>
                  <td>
                    <span className={`badge ${COR_DA_SITUACAO[situacao] ?? 'slate'}`}>{situacao}</span>
                    {pedido.saldoAberto === null ? <span className="sub-linha">Saldo em conferência</span> : null}
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={empresas.mostrar ? 5 : 4} className="rotulo-do-total">Em aberto (notas emitidas e ainda não pagas)</td>
              <td className="tnum total" data-teste="fiscal-em-aberto">{emAberto === null ? 'Pendente de conferência' : br(emAberto)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  )
}

/** "NF 38 + NF 39" · "em emissão" · "—" (spec 11.2). */
function rotuloDasNotas(pedido: PedidoDoClienteNoPortal): string {
  if (pedido.notas.length === 0) return '—'
  const emEmissao = pedido.notas.every((n) => n.situacao === 'em_emissao')
  if (emEmissao) return 'em emissão'
  return pedido.notas.map((n) => n.numero).join(' + ')
}

/** O vencimento MAIS PRÓXIMO entre as notas — é o que decide a cor da linha. */
function vencimentoDoPedido(pedido: PedidoDoClienteNoPortal): DataNominal | null {
  const datas = pedido.notas.map((n) => n.vencimento).filter((v): v is DataNominal => v !== null)
  if (datas.length === 0) return null
  return datas.reduce((menor, atual) => (atual < menor ? atual : menor))
}

/**
 * A última veiculação encerrada do acervo — a âncora do estado vazio.
 *
 * A comparação é de STRING, e é correta porque o formato é fixo: `2026-07-19` vem
 * depois de `2026-06-05` em ordem alfabética justamente porque o ano vem primeiro e o
 * mês tem dois dígitos. `new Date` aqui só traria fuso para dentro de uma frase.
 */
function ultimaVeiculacaoEncerrada(
  campanhas: readonly CampanhaDoClienteNoPortal[],
): CampanhaDoClienteNoPortal | null {
  return campanhas
    .filter((campanha) => campanha.status === 'Concluída')
    .reduce<CampanhaDoClienteNoPortal | null>(
      (maior, atual) => (maior === null || atual.fim > maior.fim ? atual : maior),
      null,
    )
}

/** O subtítulo do KPI de investimento: quais PVs estão somados ali. */
/**
 * Um "PV-nnnn" por pedido. O código da campanha é `PV-0002·C1`, colado (`0013_campanha.sql:69`):
 * cortar em `' · '` não achava o separador e listava cada campanha (PK-06A).
 */
function pvsDistintos(campanhas: readonly CampanhaDoClienteNoPortal[]): string {
  const pvs = [...new Set(campanhas.map((c) => c.codigo.split('·')[0]?.trim() ?? c.codigo))]
  if (pvs.length === 0) return 'Nenhum pedido no período'
  return pvs.join(' · ')
}

function Kpi({ rotulo, valor, apoio }: { rotulo: string; valor: string; apoio?: string }) {
  return (
    <article>
      <small>{rotulo}</small>
      <strong>{valor}</strong>
      {apoio === undefined ? null : <span className="kpi-apoio">{apoio}</span>}
    </article>
  )
}
